import React, { useState, useEffect } from 'react';
import { Plus, Loader2, Facebook, User, Link, Key, Settings, AlertCircle, Power, Trash2, CheckCircle, XCircle, UserPlus } from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import { API_URL } from '../../config';

export default function PageManagement() {
  const [pages, setPages] = useState([]);
  const [users, setUsers] = useState([]);
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showReactivateModal, setShowReactivateModal] = useState(false);
  const [pageToReactivate, setPageToReactivate] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [newPage, setNewPage] = useState({
    name: '',
    facebook_page_name: '',
    facebook_page_id: '',
    facebook_page_url: '',
    page_access_token: '',
    owner_user_id: '',
    facebook_app_id: '',
    webhook_slug: ''
  });

  useEffect(() => {
    loadPages();
    loadUsers();
    loadApps();
  }, []);

  const loadPages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('📄 Pages chargées:', data.clients);
        setPages(data.clients || []);
      }
    } catch (error) {
      toast.error('Erreur chargement pages');
    }
    setLoading(false);
  };

  const loadUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/users`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        const clientUsers = (data.users || []).filter(u => u.user_type === 'client' && u.status === 'active');
        setUsers(clientUsers);
      }
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadApps = async () => {
    try {
      const response = await fetch(`${API_URL}/api/facebook-apps/available`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setApps(data.apps || []);
      }
    } catch (error) {
      console.error('Error loading apps:', error);
    }
  };

  const createPage = async (e) => {
    e.preventDefault();
    
    if (!newPage.owner_user_id) {
      toast.error('Veuillez sélectionner un utilisateur');
      return;
    }

    if (!newPage.facebook_app_id) {
      toast.error('Veuillez sélectionner une app Facebook');
      return;
    }
    
    toast.loading('Ajout page...', { id: 'create' });
    
    try {
      const response = await fetch(`${API_URL}/api/clients`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newPage)
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Page ajoutée !', { id: 'create' });
        setShowModal(false);
        setNewPage({
          name: '',
          facebook_page_name: '',
          facebook_page_id: '',
          facebook_page_url: '',
          page_access_token: '',
          owner_user_id: '',
          facebook_app_id: ''
        });
        loadPages();
        loadApps();
      } else {
        toast.error(data.message || 'Erreur création', { id: 'create' });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: 'create' });
    }
  };

  // ⭐ NOUVEAU : Désactiver page (retire l'assignation)
  const deactivatePage = async (page) => {
    if (!window.confirm(`Désactiver "${page.facebook_page_name}" ?\n\nCette action retirera l'assignation à l'utilisateur. La page ne sera plus visible pour l'utilisateur.`)) {
      return;
    }
    
    toast.loading('Désactivation...', { id: 'deactivate' });
    
    try {
      const response = await fetch(`${API_URL}/api/clients/${page.$id}/deactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Page désactivée et désassignée !', { id: 'deactivate' });
        loadPages();
        loadApps();
      } else {
        toast.error(data.message || 'Erreur désactivation', { id: 'deactivate' });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: 'deactivate' });
    }
  };

  // ⭐ NOUVEAU : Ouvrir modal réactivation
  const openReactivateModal = (page) => {
    setPageToReactivate(page);
    setSelectedUserId('');
    setShowReactivateModal(true);
  };

  // ⭐ NOUVEAU : Réactiver et réassigner
  const reactivatePage = async (e) => {
    e.preventDefault();
    
    if (!selectedUserId) {
      toast.error('Veuillez sélectionner un utilisateur');
      return;
    }
    
    toast.loading('Réactivation...', { id: 'reactivate' });
    
    try {
      const response = await fetch(`${API_URL}/api/clients/${pageToReactivate.$id}/reactivate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ owner_user_id: selectedUserId })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast.success('Page réactivée et assignée !', { id: 'reactivate' });
        setShowReactivateModal(false);
        setPageToReactivate(null);
        setSelectedUserId('');
        loadPages();
      } else {
        toast.error(data.message || 'Erreur réactivation', { id: 'reactivate' });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: 'reactivate' });
    }
  };

  // ⭐ MODIFIÉ : Supprimer page
  const deletePage = async (pageId, pageName) => {
    if (!window.confirm(`⚠️ SUPPRIMER DÉFINITIVEMENT "${pageName}" ?\n\nCette action supprimera :\n- La page\n- Toutes les publications\n- Tous les commentaires\n- Les sessions de monitoring\n\nCette action est IRRÉVERSIBLE.`)) {
      return;
    }
    
    toast.loading('Suppression...', { id: 'delete' });
    
    try {
      const response = await fetch(`${API_URL}/api/clients/${pageId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` }
      });
      
      if (response.ok) {
        toast.success('Page supprimée définitivement !', { id: 'delete' });
        loadPages();
        loadApps();
      } else {
        const data = await response.json();
        toast.error(data.message || 'Erreur suppression', { id: 'delete' });
      }
    } catch (error) {
      toast.error('Erreur réseau', { id: 'delete' });
    }
  };

  const getUserName = (userId) => {
    if (!userId) return 'Non assigné';
    const user = users.find(u => u.user_id === userId);
    return user ? user.name : 'Utilisateur inconnu';
  };

  const getAppName = (appId) => {
    if (!appId) return 'Aucune app';
    const app = apps.find(a => a.$id === appId);
    return app ? app.app_name : 'App inconnue';
  };

  const getSelectedAppInfo = () => {
    const app = apps.find(a => a.$id === newPage.facebook_app_id);
    return app;
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">Gestion Pages Facebook</h2>
          <p className="text-blue-200">Ajouter et assigner des pages aux utilisateurs</p>
        </div>
        
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white transition-all hover:scale-105"
        >
          <Plus className="w-5 h-5" />
          Nouvelle Page
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
        </div>
      ) : pages.length === 0 ? (
        <div className="text-center py-12 bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl">
          <Facebook className="w-16 h-16 text-blue-400 mx-auto mb-4 opacity-50" />
          <h3 className="text-xl font-bold text-white mb-2">Aucune page</h3>
          <p className="text-blue-200 mb-6">Ajoutez votre première page Facebook</p>
          <button
            onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white transition-all"
          >
            <Plus className="w-5 h-5" />
            Nouvelle Page
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {pages.map((page) => (
            <div key={page.$id} className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-6 hover:border-white/20 transition-all">
              {/* Header avec actions */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 ${
                    page.status === 'active'
                      ? 'bg-gradient-to-br from-blue-500 to-purple-500'
                      : 'bg-gradient-to-br from-gray-600 to-gray-700'
                  }`}>
                    <Facebook className="w-8 h-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-white mb-1">{page.facebook_page_name}</h3>
                    <p className="text-sm text-blue-200 mb-2">{page.name}</p>
                    
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-sm text-purple-300">
                        <User className="w-4 h-4" />
                        <span className="font-semibold">{getUserName(page.owner_user_id)}</span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-cyan-300">
                        <Settings className="w-4 h-4" />
                        <span className="font-semibold">{getAppName(page.facebook_app_id)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ⭐ BOUTONS D'ACTIONS MODIFIÉS */}
                <div className="flex flex-col gap-2">
                  {page.status === 'active' ? (
                    <button
                      onClick={() => deactivatePage(page)}
                      className="p-2 bg-orange-500/20 hover:bg-orange-500/30 rounded-lg text-orange-300 transition-all"
                      title="Désactiver (retire l'assignation)"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => openReactivateModal(page)}
                      className="p-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg text-green-300 transition-all"
                      title="Réactiver et assigner"
                    >
                      <UserPlus className="w-5 h-5" />
                    </button>
                  )}
                  
                  <button
                    onClick={() => deletePage(page.$id, page.facebook_page_name)}
                    className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg text-red-300 transition-all"
                    title="Supprimer définitivement"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center gap-2 text-sm text-blue-200">
                  <Link className="w-4 h-4" />
                  <a href={page.facebook_page_url} target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors truncate">
                    {page.facebook_page_url}
                  </a>
                </div>
                <div className="text-sm text-blue-200">
                  ID: {page.facebook_page_id}
                </div>

                  <div>
                    <label className="block text-blue-200 text-sm font-medium mb-2">
                      Webhook Slug (pour ManyChat)
                    </label>
                    <input
                      type="text"
                      value={newPage.webhook_slug || ''}
                      onChange={(e) => setNewPage({...newPage, webhook_slug: e.target.value})}
                      className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
                      placeholder="restaurant-tana"
                    />
                    <p className="text-xs text-blue-300 mt-1">
                      Optionnel. Si vide, utilisera le Page ID. Format: lettres-minuscules-tirets
                    </p>
                  </div>
              </div>

              {/* Statut */}
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                page.status === 'active'
                  ? 'bg-green-500/20 text-green-300 border border-green-500/30'
                  : 'bg-gray-500/20 text-gray-300 border border-gray-500/30'
              }`}>
                <div className={`w-2 h-2 rounded-full ${page.status === 'active' ? 'bg-green-400 animate-pulse' : 'bg-gray-400'}`} />
                {page.status === 'active' ? 'Active' : 'Inactive'}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Création */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-2xl font-bold text-white mb-6">Nouvelle Page Facebook</h3>
            
            <form onSubmit={createPage} className="space-y-4">
              {/* Sélection App */}
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  App Facebook à utiliser *
                </label>
                <select
                  value={newPage.facebook_app_id}
                  onChange={(e) => setNewPage({...newPage, facebook_app_id: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" className="bg-slate-900">Sélectionnez une app</option>
                  {apps.map((app) => (
                    <option 
                      key={app.$id} 
                      value={app.$id} 
                      disabled={app.is_full}
                      className="bg-slate-900"
                    >
                      {app.app_name} ({app.current_clients}/{app.max_clients} clients)
                      {app.is_full ? ' - PLEINE' : ''}
                    </option>
                  ))}
                </select>
                
                {newPage.facebook_app_id && getSelectedAppInfo() && (
                  <div className="mt-2 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <div className="text-xs text-blue-300 space-y-1">
                      <div>✅ Slots disponibles: {getSelectedAppInfo().available_slots}</div>
                      <div>📡 Webhook: {getSelectedAppInfo().webhook_url}</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Instructions */}
              {newPage.facebook_app_id && (
                <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                  <h4 className="text-purple-300 font-semibold mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Comment générer le token ?
                  </h4>
                  <ol className="text-sm text-purple-200 space-y-1 list-decimal list-inside">
                    <li>Allez sur developers.facebook.com</li>
                    <li>Ouvrez l'app "{getSelectedAppInfo()?.app_name}"</li>
                    <li>Tools → Graph API Explorer</li>
                    <li>Sélectionnez la page Facebook du client</li>
                    <li>Permissions : pages_manage_posts, pages_read_engagement</li>
                    <li>Cliquez "Generate Access Token"</li>
                    <li>Copiez le token ci-dessous</li>
                  </ol>
                </div>
              )}

              {/* Sélection Utilisateur */}
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Assigner à l'utilisateur *
                </label>
                <select
                  value={newPage.owner_user_id}
                  onChange={(e) => setNewPage({...newPage, owner_user_id: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" className="bg-slate-900">Sélectionnez un utilisateur</option>
                  {users.map((user) => (
                    <option key={user.$id} value={user.user_id} className="bg-slate-900">
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              {/* Reste des champs... */}
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">Nom du client</label>
                <input
                  type="text"
                  value={newPage.name}
                  onChange={(e) => setNewPage({...newPage, name: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Nom entreprise"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <Facebook className="w-4 h-4" />
                  Nom de la page Facebook
                </label>
                <input
                  type="text"
                  value={newPage.facebook_page_name}
                  onChange={(e) => setNewPage({...newPage, facebook_page_name: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Ex: TDI Digital MG"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2">Page ID</label>
                <input
                  type="text"
                  value={newPage.facebook_page_id}
                  onChange={(e) => setNewPage({...newPage, facebook_page_id: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="712328321973160"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <Link className="w-4 h-4" />
                  URL de la page
                </label>
                <input
                  type="url"
                  value={newPage.facebook_page_url}
                  onChange={(e) => setNewPage({...newPage, facebook_page_url: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="https://facebook.com/tdidigitalmg"
                  required
                />
              </div>

              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <Key className="w-4 h-4" />
                  Access Token
                </label>
                <textarea
                  value={newPage.page_access_token}
                  onChange={(e) => setNewPage({...newPage, page_access_token: e.target.value})}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-blue-300/50 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none font-mono text-sm"
                  rows="3"
                  placeholder="EAAxxxx..."
                  required
                />
                <p className="text-xs text-blue-300 mt-1">
                  ⚠️ Ce token doit être généré depuis l'app sélectionnée ci-dessus
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-xl text-white transition-all"
                >
                  Ajouter & Assigner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ⭐ NOUVEAU : Modal Réactivation */}
      {showReactivateModal && pageToReactivate && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-white/20 rounded-2xl p-8 max-w-md w-full">
            <h3 className="text-2xl font-bold text-white mb-2">Réactiver la page</h3>
            <p className="text-blue-200 mb-6">{pageToReactivate.facebook_page_name}</p>
            
            <form onSubmit={reactivatePage} className="space-y-4">
              <div>
                <label className="block text-blue-200 text-sm font-medium mb-2 flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Assigner à l'utilisateur *
                </label>
                <select
                  value={selectedUserId}
                  onChange={(e) => setSelectedUserId(e.target.value)}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                >
                  <option value="" className="bg-slate-900">Sélectionnez un utilisateur</option>
                  {users.map((user) => (
                    <option key={user.$id} value={user.user_id} className="bg-slate-900">
                      {user.name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4">
                <p className="text-sm text-green-200">
                  ✅ Cette action réactivera la page et l'assignera à l'utilisateur sélectionné.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowReactivateModal(false);
                    setPageToReactivate(null);
                    setSelectedUserId('');
                  }}
                  className="flex-1 px-4 py-3 bg-white/10 hover:bg-white/20 rounded-xl text-white transition-all"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 rounded-xl text-white transition-all"
                >
                  Réactiver & Assigner
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}